import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";

const SORTS = [
  { v: "new", l: "Newest" }, { v: "rating", l: "Top rated" },
  { v: "price_low", l: "Price: Low to High" }, { v: "price_high", l: "Price: High to Low" },
];

// Approx festival dates [month(1-12), day] — used only for seasonal ordering, not exact.
const FESTIVAL_DATES = {
  "New Year": [1, 1], "Lohri": [1, 13], "Makar Sankranti": [1, 14], "Pongal": [1, 15],
  "Valentine's Day": [2, 14], "Holi": [3, 14], "Eid": [3, 31], "Baisakhi": [4, 14],
  "Raksha Bandhan": [8, 9], "Ganesh Chaturthi": [8, 27], "Onam": [9, 5],
  "Navratri": [10, 3], "Durga Puja": [10, 10], "Dussehra": [10, 12],
  "Karwa Chauth": [10, 29], "Diwali": [11, 8], "Bhai Dooj": [11, 10], "Christmas": [12, 25],
};

// Sort festivals so the temporally-closest upcoming festival floats to the top.
function seasonalSort(festivals) {
  const today = new Date();
  const daysUntil = (name) => {
    const d = FESTIVAL_DATES[name];
    if (!d) return Infinity;
    let dt = new Date(today.getFullYear(), d[0] - 1, d[1]);
    if (dt < new Date(today.getFullYear(), today.getMonth(), today.getDate())) {
      dt = new Date(today.getFullYear() + 1, d[0] - 1, d[1]);
    }
    return Math.round((dt - today) / 86400000);
  };
  return [...festivals]
    .map((f) => ({ name: f, days: daysUntil(f) }))
    .sort((a, b) => a.days - b.days);
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [festivals, setFestivals] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [maxPrice, setMaxPrice] = useState("");

  const get = (k) => params.get(k) || "";
  const sort = get("sort") || "new";

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data));
    api.get("/occasions").then(({ data }) => setOccasions(data));
    api.get("/taxonomy").then(({ data }) => {
      setRecipients(data.recipients || []);
      setFestivals(seasonalSort(data.festivals || []));
    });
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const p = {};
    ["category", "occasion", "recipient", "festival", "search"].forEach((k) => { if (get(k)) p[k] = get(k); });
    p.sort = sort;
    const { data } = await api.get("/products", { params: p });
    setProducts(data);
    setLoading(false);
  }, [params]); // eslint-disable-line

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v && get(k) !== v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const clearAll = () => { setParams(new URLSearchParams()); setMaxPrice(""); };
  const activeCount = ["category", "occasion", "recipient", "festival", "search"].filter((k) => get(k)).length + (maxPrice ? 1 : 0);

  const shown = maxPrice ? products.filter((p) => (p.discount ? p.price * (1 - p.discount / 100) : p.price) <= Number(maxPrice)) : products;

  const CatNode = ({ cat }) => {
    const isActive = get("category") === cat.slug;
    const open = expanded[cat.slug] ?? isActive ?? false;
    const childActive = cat.children?.some((c) => get("category") === c.slug);
    return (
      <div>
        <div className="flex items-center justify-between">
          <button onClick={() => setFilter("category", cat.slug)}
            className={`text-sm py-1.5 flex-1 text-left ${isActive ? "text-clay font-semibold" : "text-ink/80 hover:text-clay"}`}
            data-testid={`cat-${cat.slug}`}>
            {cat.name} <span className="text-mute text-xs">({cat.count})</span>
          </button>
          {cat.children?.length > 0 && (
            <button onClick={() => setExpanded({ ...expanded, [cat.slug]: !open })} className="p-1 text-mute" data-testid={`cat-expand-${cat.slug}`}>
              <ChevronDown className={`w-4 h-4 transition-transform ${open || childActive ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>
        {cat.children?.length > 0 && (open || childActive) && (
          <div className="ml-3 pl-2 border-l border-line space-y-0.5 mb-1">
            {cat.children.map((c) => (
              <button key={c.slug} onClick={() => setFilter("category", c.slug)}
                className={`block text-sm py-1 text-left w-full ${get("category") === c.slug ? "text-clay font-semibold" : "text-mute hover:text-clay"}`}
                data-testid={`cat-${c.slug}`}>
                {c.name} <span className="text-xs">({c.count})</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const FilterPanel = () => (
    <>
      <div className="mb-6">
        <div className="overline text-mute mb-3">Category</div>
        <div className="space-y-0.5">{categories.map((c) => <CatNode key={c.slug} cat={c} />)}</div>
      </div>
      <div className="mb-6">
        <div className="overline text-mute mb-3">Occasion</div>
        <div className="flex flex-wrap gap-2">
          {occasions.map((o) => (
            <button key={o.slug} onClick={() => setFilter("occasion", o.slug)}
              className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${get("occasion") === o.slug ? "bg-clay text-sand border-clay" : "border-line text-ink/80 hover:border-clay"}`}
              data-testid={`occ-${o.slug}`}>{o.name} ({o.count})</button>
          ))}
        </div>
      </div>
      <div className="mb-6">
        <div className="overline text-mute mb-3">Recipient</div>
        <div className="flex flex-wrap gap-2">
          {recipients.map((r) => (
            <button key={r} onClick={() => setFilter("recipient", r)}
              className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${get("recipient") === r ? "bg-clay text-sand border-clay" : "border-line text-ink/80 hover:border-clay"}`}
              data-testid={`recipient-${r.toLowerCase().replace(/\s/g, "-")}`}>{r}</button>
          ))}
        </div>
      </div>
      <div className="mb-6">
        <div className="overline text-mute mb-3">Festival</div>
        <div className="flex flex-wrap gap-2">
          {festivals.map((f, i) => {
            const active = get("festival") === f.name;
            const soon = i === 0 && f.days !== Infinity;
            return (
              <button key={f.name} onClick={() => setFilter("festival", f.name)}
                className={`text-sm px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5 ${active ? "bg-clay text-sand border-clay" : "border-line text-ink/80 hover:border-clay"}`}
                data-testid={`festival-${f.name.toLowerCase().replace(/[\s']/g, "-")}`}>
                {f.name}
                {soon && <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full ${active ? "bg-sand/25 text-sand" : "bg-terra/15 text-terra"}`}>Soon</span>}
              </button>
            );
          })}
        </div>
      </div>
      <div className="mb-6">
        <div className="overline text-mute mb-3">Max price</div>
        <input type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="e.g. 1000"
          className="w-full bg-surface border border-line rounded-sm px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="price-max-input" />
      </div>
    </>
  );

  const heading = get("search") ? `“${get("search")}”`
    : get("category") ? (categories.flatMap((c) => [c, ...(c.children || [])]).find((c) => c.slug === get("category"))?.name || "Category")
    : get("occasion") ? (occasions.find((o) => o.slug === get("occasion"))?.name || "Occasion")
    : get("recipient") || get("festival") || "All handmade gifts";

  return (
    <div className="pt-24 sm:pt-28 pb-24 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="overline text-clay mb-3">The collection</div>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h1 className="font-serif text-4xl sm:text-6xl tracking-tighter">{heading}</h1>
            <div className="flex items-center gap-3">
              <select value={sort} onChange={(e) => setFilter("sort", e.target.value)}
                className="text-sm bg-surface border border-line rounded-full px-4 py-2 outline-none focus:ring-2 focus:ring-clay" data-testid="sort-select">
                {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
              <button onClick={() => setShowFilters(true)} className="lg:hidden flex items-center gap-2 text-sm bg-clay text-sand rounded-full px-4 py-2" data-testid="open-filters-btn">
                <SlidersHorizontal className="w-4 h-4" /> Filters{activeCount > 0 && ` (${activeCount})`}
              </button>
            </div>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-[260px_1fr] gap-10 mt-10">
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <div className="flex items-center justify-between mb-5">
                <span className="font-serif text-xl">Filters</span>
                {activeCount > 0 && <button onClick={clearAll} className="text-xs text-clay link-underline" data-testid="clear-filters-btn">Clear Filters</button>}
              </div>
              <FilterPanel />
            </div>
          </aside>

          <div>
            <p className="text-sm text-mute mb-6" data-testid="results-count">{loading ? "Loading…" : `${shown.length} handmade ${shown.length === 1 ? "gift" : "gifts"}`}</p>
            {!loading && shown.length === 0 ? (
              <div className="py-24 text-center text-mute">No gifts match these filters yet. <button onClick={clearAll} className="text-clay link-underline">Clear Filters</button></div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
                {shown.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-ink/40" onClick={() => setShowFilters(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-[85%] max-w-sm bg-sand p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <span className="font-serif text-2xl">Filters</span>
              <button onClick={() => setShowFilters(false)} data-testid="close-filters-btn"><X className="w-6 h-6" /></button>
            </div>
            <FilterPanel />
            <div className="flex gap-3 mt-6">
              <button onClick={clearAll} className="flex-1 rounded-full border border-clay text-clay py-2.5 text-sm">Clear Filters</button>
              <button onClick={() => setShowFilters(false)} className="flex-1 rounded-full bg-clay text-sand py-2.5 text-sm">Show {shown.length}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
